import React from 'react';
import clsx from 'clsx';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import MenuListItem from '@material-ui/core/MenuItem';
import Typography from '@material-ui/core/Typography';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import RadioGroup from '@material-ui/core/RadioGroup';
import Radio from '@material-ui/core/Radio';
import Tooltip from '@material-ui/core/Tooltip';
import Button from '@material-ui/core/Button';
import LinearProgress from '@material-ui/core/LinearProgress';

import { Dialog, Input, showToast, Scrollbars } from '@shared/components';
import { SharedService } from '@consumers/services';
import { getFileExtension, SUPPORTED_FILE_FORMATS } from '@utils';

import {
  VALIDATIONS,
  TOAST_TYPES,
  FEEDBACK_TYPES,
  THEME_VARIANT,
} from '@shared/constants';
import { AppContext, LoaderContext } from '@shared/contexts';

import Feedback from '@assets/icons/feedback.svg';
import AltBrandLogo from '@assets/icons/brand-logo-alt.png';
import BrandLogo from '@assets/icons/brand-logo.png';
import SuccessTickIcon from '@assets/icons/done-green.svg';
import RemoveIcon from '@assets/icons/cross-red.svg';

import UserStoryInput from './user-story-input';

import useStyles from './style';

const initialState = {
  type: FEEDBACK_TYPES.ISSUE,
  issueTitle: '',
  issueDescription: '',
  userStoryDescription: '',
  userStoryGoal: '',
  userStoryReason: '',
  isModalOpen: false,
  formReset: false,
  errorList: { userStory: false },
  attachedFiles: [],
  isFileUploaded: false,
  issueAccount: '',
};

const FeedbackModal = ({
  classes: passedClasses = {},
  version,
  currentWorkspace = {},
}) => {
  const classes = useStyles();
  const { showLoader, hideLoader } = React.useContext(LoaderContext);
  const { selectedThemeVariant } = React.useContext(AppContext);
  const [state, setState] = React.useState(initialState);

  React.useEffect(() => {
    if (state.isModalOpen && currentWorkspace.uid) {
      setState(prevState => ({
        ...prevState,
        issueAccount: `${currentWorkspace.name}-${currentWorkspace.uid}`,
      }));
    }
  }, [state.isModalOpen]);

  const handleFileChange = async e => {
    const files = e.target.files;
    const filesArray = fileListToArray(files);
    // In megabytes
    const maxAllowedFileSize = 4.7;
    const [
      supportedFiles,
      unSupportedFile,
      duplicateFile,
      sizeExtendedFiles,
    ] = filesArray.reduce(
      (acc, file) => {
        const extension = getFileExtension(file.name);
        if (
          !SUPPORTED_FILE_FORMATS.FEEDBACK.includes(extension.toLowerCase())
        ) {
          acc[1].push(extension);
        } else if (
          state.attachedFiles.length &&
          state.attachedFiles.find(item => item.file.name === file.name)
        ) {
          acc[2].push(file.name);
        } else if (file.size > maxAllowedFileSize * 1048576) {
          acc[3].push(file.name);
        } else {
          acc[0].push({
            file: file,
            status: 'isUploading',
          });
        }
        return acc;
      },
      [[], [], [], []]
    );
    sizeExtendedFiles.length &&
      showToast(
        `Size of ${sizeExtendedFiles.join(', ')} is more than 4.7Mb.`,
        TOAST_TYPES.ERROR
      );

    unSupportedFile.length &&
      showToast(
        `${unSupportedFile.join(', ')} extension not supported.`,
        TOAST_TYPES.ERROR
      );

    duplicateFile.length &&
      showToast(
        `${duplicateFile.join(', ')} already uploaded.`,
        TOAST_TYPES.ERROR
      );
    supportedFiles.length &&
      setState(prevState => {
        return {
          ...prevState,
          attachedFiles: prevState.attachedFiles.length
            ? [...supportedFiles, ...prevState.attachedFiles]
            : supportedFiles,
        };
      });
    setTimeout(() => {
      setState(prevState => {
        return {
          ...prevState,
          isFileUploaded: true,
          attachedFiles: prevState.attachedFiles.map(file => {
            file.status = 'success';
            return file;
          }),
        };
      });
    }, 2000);
  };

  const fileListToArray = list => {
    const array = [];
    for (var i = 0; i < list.length; i++) {
      array.push(list.item(i));
    }
    return array;
  };
  const getFileUploadStatus = status => {
    if (status === 'success') {
      return (
        <Tooltip title='Upload Successful' placement='top-start'>
          <img className='' alt='' src={SuccessTickIcon} />
        </Tooltip>
      );
    } else if (status === 'failed') {
      return (
        <Tooltip title='Upload Failed.Try Again' placement='top-start'>
          <img className='' alt='' src={RemoveIcon} />
        </Tooltip>
      );
    } else if (status === 'isUploading') {
      return (
        <Tooltip title='File is uploading...' placement='top-start'>
          <span className={classes.progress}>
            <LinearProgress variant={'indeterminate'} />
          </span>
        </Tooltip>
      );
    }
  };

  const removeAttachedFile = index => {
    const attachedFilesClone = state.attachedFiles.slice();
    attachedFilesClone.splice(index, 1);
    setState(prevState => {
      return {
        ...prevState,
        attachedFiles: attachedFilesClone,
      };
    });
  };

  const handleUserStoryChange = (value, name, error) => {
    setState(prevState => {
      return {
        ...prevState,
        [name]: value,
        errorList: {
          ...prevState.errorList,
          [name === 'userStoryGoal' || name === 'userStoryReason'
            ? 'userStory'
            : name]: error,
        },
      };
    });
  };

  const handleSendFeedback = async () => {
    showLoader();
    const payload = {
      type: state.type,
      title:
        state.type === FEEDBACK_TYPES.ISSUE
          ? state.issueTitle
          : `As a user, I want ${state.userStoryGoal.trim()} so that ${state.userStoryReason.trim()}.`,
      description:
        state.type === FEEDBACK_TYPES.ISSUE
          ? state.issueDescription
          : state.userStoryDescription,
    };
    if (state.type === FEEDBACK_TYPES.ISSUE) {
      payload['account'] = state.issueAccount;
    }
    const { data, error } = await SharedService.createTicket(payload);

    if (error) {
      showToast(
        Array.isArray(error) ? error[0].message : error,
        TOAST_TYPES.ERROR
      );
      hideLoader();
      return;
    }
    if (state.attachedFiles.length) {
      uploadFilesOnSubmit(data.id);
    } else {
      showToast('Feedback submitted successfully.', TOAST_TYPES.SUCCESS);
      setState({
        ...initialState,
        type: state.type,
        isModalOpen: true,
        formReset: true,
      });
      setTimeout(
        () =>
          setState(prevState => {
            return {
              ...prevState,
              formReset: false,
              issueAccount: currentWorkspace.uid
                ? `${currentWorkspace.name}-${currentWorkspace.uid}`
                : '',
            };
          }),
        1000
      );
    }
    hideLoader();
  };

  const uploadFilesOnSubmit = async id => {
    let index = 0;
    if (state.attachedFiles.length > 0) {
      while (index < state.attachedFiles.length) {
        showLoader();
        var formData = new FormData();
        formData.append('file', state.attachedFiles[index].file);
        try {
          const { error } = await SharedService.uploadFiles(formData, id);
        } catch (exceptionCode) {
          showToast('Cannot upload file successfully.', TOAST_TYPES.ERROR);
          setState(prevState => {
            return {
              ...prevState,
              attachedFiles: prevState.attachedFiles.map((file, indx) => {
                if (index === indx) {
                  file.status = 'failed';
                }
                return file;
              }),
            };
          });
          hideLoader();
          return;
        }
        setState(prevState => {
          return {
            ...prevState,
            attachedFiles: prevState.attachedFiles.map((file, indx) => {
              if (index === indx) {
                file.status = 'success';
              }
              return file;
            }),
          };
        });
        hideLoader();
        index++;
      }
      showToast('Feedback submitted successfully.', TOAST_TYPES.SUCCESS);
      setState({
        ...initialState,
        type: state.type,
        isModalOpen: true,
        formReset: true,
      });
      setTimeout(
        () =>
          setState(prevState => {
            return {
              ...prevState,
              formReset: false,
              issueAccount: currentWorkspace.uid
                ? `${currentWorkspace.name}-${currentWorkspace.uid}`
                : '',
            };
          }),
        1000
      );
    }
  };

  const handleModalClose = () => {
    setState(initialState);
  };

  const isSendBtnDisable =
    state.type === FEEDBACK_TYPES.ISSUE
      ? !state.issueTitle ||
        !state.issueDescription ||
        state.errorList.issueAccount ||
        state.errorList.issueTitle ||
        state.errorList.issueDescription
      : !state.userStoryDescription ||
        !state.userStoryGoal ||
        !state.userStoryReason ||
        state.errorList.userStoryDescription ||
        state.errorList.userStoryGoal ||
        state.errorList.userStoryReason;
  return (
    <>
      <MenuListItem
        onClick={() =>
          setState(prevState => {
            return {
              ...prevState,
              isModalOpen: true,
            };
          })
        }
      >
        <ListItemIcon className={passedClasses.icon}>
          <img src={Feedback} alt='' width={16} />
        </ListItemIcon>
        <ListItemText className={passedClasses.label} primary='Give Feedback' />
      </MenuListItem>
      <Dialog
        classes={{
          paper: classes.feedbackDialog,
        }}
        open={state.isModalOpen}
        onClose={handleModalClose}
      >
        <Dialog.Title hasClose />
        <Dialog.Content>
          <Scrollbars>
            <div className='d-flex flex-column f-justify-between'>
              <img
                className='ml-6 mb-6'
                src={
                  selectedThemeVariant === THEME_VARIANT.LIGHT
                    ? AltBrandLogo
                    : BrandLogo
                }
                height={42}
                width={210}
              />
              <Typography className='mb-4' variant='subtitle2'>
                Release {version.release}, Build {version.build}
              </Typography>
              <Typography className='mb-4 medium-font' variant='h5'>
                Give Us Feedback!
              </Typography>
              <Typography variant='body2' className='mb-4 medium-font'>
                Your feedback is valuable and we need your insights! Please fill
                out the form below to give us your thoughts or experiences with
                this application!
              </Typography>
              <div className='w-75'>
                <Typography className='mb-4' variant='subtitle2'>
                  Work Item Type
                </Typography>
                <div>
                  <FormControl className='time-format'>
                    <RadioGroup
                      className='d-flex flex-row mb-4'
                      value={state.type}
                      onChange={event =>
                        setState(prevState => {
                          return {
                            ...prevState,
                            type: event.currentTarget.value,
                            attachedFiles: [],
                          };
                        })
                      }
                    >
                      <FormControlLabel
                        value={FEEDBACK_TYPES.ISSUE}
                        control={<Radio color='primary' />}
                        label={
                          <div className='d-flex f-align-center f-jusitfy-center'>
                            <Typography className='mr-3' variant='body2'>
                              {FEEDBACK_TYPES.ISSUE}
                            </Typography>
                          </div>
                        }
                        labelPlacement='end'
                      />
                      <FormControlLabel
                        value={FEEDBACK_TYPES.USER_STORY}
                        control={<Radio color='primary' />}
                        label={
                          <div className='d-flex f-align-center f-jusitfy-center'>
                            <Typography variant='body2'>
                              {FEEDBACK_TYPES.USER_STORY}
                            </Typography>
                          </div>
                        }
                        labelPlacement='end'
                      />
                    </RadioGroup>
                  </FormControl>
                </div>
                {state.type === FEEDBACK_TYPES.ISSUE ? (
                  <>
                    <Input
                      disabled={!!currentWorkspace.uid}
                      type='text'
                      label='Account name or UID'
                      placeholder='Account name or UID'
                      name='issueAccount'
                      value={state.issueAccount}
                      onChange={(event, error) => {
                        let value = event.currentTarget.value;
                        let errorField = event.currentTarget.name;

                        setState(prevState => {
                          return {
                            ...prevState,
                            issueAccount: value,
                            errorList: {
                              ...prevState.errorList,
                              [errorField]: !!error,
                            },
                          };
                        });
                      }}
                      validations={{
                        [VALIDATIONS.MAX_LENGTH]: 255,
                      }}
                      className='w-100 mb-4'
                    />
                    <Input
                      required
                      type='text'
                      label='Title'
                      placeholder='Title'
                      name='issueTitle'
                      value={state.issueTitle}
                      onChange={(event, error) => {
                        let value = event.currentTarget.value;
                        let errorField = event.currentTarget.name;

                        setState(prevState => {
                          return {
                            ...prevState,
                            issueTitle: value,
                            errorList: {
                              ...prevState.errorList,
                              [errorField]: !!error,
                            },
                          };
                        });
                      }}
                      validations={{
                        [VALIDATIONS.REQUIRED]: state.formReset ? false : true,
                        [VALIDATIONS.MAX_LENGTH]: 255,
                      }}
                      className='w-100 mb-4'
                    />
                    <Input
                      required
                      variant='outlined'
                      multiline
                      rows={4}
                      name='issueDescription'
                      label='Please provide a detailed description.'
                      value={state.issueDescription}
                      placeholder='Please provide a detailed description.'
                      onChange={(event, error) => {
                        let value = event.currentTarget.value;
                        let errorField = event.currentTarget.name;

                        setState(prevState => {
                          return {
                            ...prevState,
                            issueDescription: value,
                            errorList: {
                              ...prevState.errorList,
                              [errorField]: !!error,
                            },
                          };
                        });
                      }}
                      validations={{
                        [VALIDATIONS.REQUIRED]: state.formReset ? false : true,
                      }}
                      className='w-100 mb-4'
                    />
                  </>
                ) : (
                  <UserStoryInput
                    userStoryDescription={state.userStoryDescription}
                    userStoryGoal={state.userStoryGoal}
                    userStoryReason={state.userStoryReason}
                    handleUserStoryChange={handleUserStoryChange}
                    formReset={state.formReset}
                  />
                )}
                <div>
                  <input
                    multiple
                    type='file'
                    name='myFile'
                    id='feedbackId'
                    placeholder={null}
                    className='d-none mb-6'
                    onChange={handleFileChange}
                    onClick={event => (event.currentTarget.value = '')}
                    accept='.pdf, .jpg, .jpeg, .png, .gif, .xlsx , .csv, video/mp4 , video/x-m4v, video/*'
                  />
                  <Button
                    color='primary'
                    variant='outlined'
                    htmlFor='feedbackId'
                    component='label'
                  >
                    Add Attachment
                  </Button>
                </div>
              </div>
              <div className={classes.fileList}>
                {state.attachedFiles &&
                  state.attachedFiles.map((item, index) => (
                    <div
                      className={clsx(
                        'd-flex f-align-center f-justify-between p-2 '
                      )}
                    >
                      <Tooltip title={item.file.name} placement='top-start'>
                        <Typography
                          variant='body2'
                          className='color-text-secondary w-100 mr-4'
                          noWrap
                        >
                          {item.file.name}
                        </Typography>
                      </Tooltip>
                      <div
                        className={clsx({
                          [classes.progressBar]: item.status === 'isUploading',
                        })}
                      >
                        {getFileUploadStatus(item.status)}
                      </div>
                      {item.status === 'success' && (
                        <span className={classes.removeButton}>
                          <Button
                            size='small'
                            classes={{
                              root: 'border-error',
                              label: 'color-error',
                            }}
                            className={clsx('ml-4')}
                            variant='outlined'
                            onClick={() => removeAttachedFile(index)}
                          >
                            Remove
                          </Button>
                        </span>
                      )}
                    </div>
                  ))}
              </div>
            </div>
          </Scrollbars>
        </Dialog.Content>
        <Dialog.Actions>
          <div className='mb-3'>
            <Button
              className='text-secondary cancel-btn mr-6'
              onClick={handleModalClose}
            >
              Cancel
            </Button>
            <Button
              variant='contained'
              color='primary'
              className='mr-3'
              onClick={handleSendFeedback}
              disabled={isSendBtnDisable}
            >
              Send
            </Button>
          </div>
        </Dialog.Actions>
      </Dialog>
    </>
  );
};
export default FeedbackModal;
